import { Inject, Injectable } from '@angular/core';
import { DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import { UiAuthService } from '../legacy/ui';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import moment from 'moment-timezone';
import { Moment } from 'moment-timezone';

// Like MomentDateAdapter, but creates Moments in the current user's time zone
// as specified in their user profile (as opposed to UTC or system local time).
@Injectable()
export class ZonedMomentDateAdapter extends DateAdapter<Moment, string> {
  constructor(
    private baseAdapter: MomentDateAdapter,
    private authService: UiAuthService,
    @Inject(MAT_DATE_LOCALE) protected locale: string,
  ) {
    super();
  }
  getYear(date: moment.Moment): number {
    return this.baseAdapter.getYear(date);
  }
  getMonth(date: moment.Moment): number {
    return this.baseAdapter.getMonth(date);
  }
  getDate(date: moment.Moment): number {
    return this.baseAdapter.getDate(date);
  }
  getDayOfWeek(date: moment.Moment): number {
    return this.baseAdapter.getDayOfWeek(date);
  }
  getMonthNames(style: 'long' | 'short' | 'narrow'): string[] {
    return this.baseAdapter.getMonthNames(style);
  }
  getDateNames(): string[] {
    return this.baseAdapter.getDateNames();
  }
  getDayOfWeekNames(style: 'long' | 'short' | 'narrow'): string[] {
    return this.baseAdapter.getDayOfWeekNames(style);
  }
  getYearName(date: moment.Moment): string {
    return this.baseAdapter.getYearName(date);
  }
  getFirstDayOfWeek(): number {
    return this.baseAdapter.getFirstDayOfWeek();
  }
  getNumDaysInMonth(date: moment.Moment): number {
    return this.baseAdapter.getNumDaysInMonth(date);
  }
  clone(date: moment.Moment): moment.Moment {
    return this.baseAdapter.clone(date);
  }
  format(date: moment.Moment, displayFormat: string): string {
    // Ensure use of day/month/year order for all locales, including locales
    // that otherwise use month/day/year by default.
    // Also ensure leading zeros are added consistently with legacy fields.
    // See https://momentjs.com/docs/#/displaying/format/
    displayFormat = 'DD/MM/YYYY';
    return this.baseAdapter.format(
      date.clone().tz(this.authService.currentUser.timeZone),
      displayFormat,
    );
  }
  addCalendarYears(date: moment.Moment, years: number): moment.Moment {
    return this.baseAdapter.addCalendarYears(date, years);
  }
  addCalendarMonths(date: moment.Moment, months: number): moment.Moment {
    return this.baseAdapter.addCalendarMonths(date, months);
  }
  addCalendarDays(date: moment.Moment, days: number): moment.Moment {
    return this.baseAdapter.addCalendarDays(date, days);
  }
  toIso8601(date: moment.Moment): string {
    return this.baseAdapter.toIso8601(date);
  }
  isDateInstance(obj: any): boolean {
    return this.baseAdapter.isDateInstance(obj);
  }
  isValid(date: moment.Moment): boolean {
    return this.baseAdapter.isValid(date);
  }
  invalid(): moment.Moment {
    return this.baseAdapter.invalid();
  }

  getValidDateOrNull(obj: unknown): Moment | null {
    return this.baseAdapter.getValidDateOrNull(obj);
  }

  deserialize(value: any): Moment | null {
    return this.baseAdapter.deserialize(value);
  }

  setLocale(locale: string): void {
    this.baseAdapter.setLocale(locale);
    this.locale = locale;
  }

  compareDate(a: Moment, b: Moment): number {
    return this.baseAdapter.compareDate(a, b);
  }

  sameDate(a: Moment | null, b: Moment | null): boolean {
    return this.baseAdapter.sameDate(a, b);
  }

  clampDate(d: Moment, min?: Moment | null, max?: Moment | null): Moment {
    return this.baseAdapter.clampDate(d, min, max);
  }

  createDate(year: number, month: number, day: number): Moment {
    return moment
      .tz({ year, month, day }, this.authService.currentUser.timeZone)
      .locale(this.locale);
  }

  today(): Moment {
    return moment()
      .tz(this.authService.currentUser.timeZone)
      .locale(this.locale);
  }

  parse(value: string, parseFormat: string): Moment | null {
    if (value === '') {
      return null;
    }
    return moment
      .tz(value, parseFormat, this.authService.currentUser.timeZone)
      .locale(this.locale);
  }
}
